import {
  BRAND, MAX_SEATS, changedFromDefaults, describeRules, nickname,
  type RuleLine, type WireState,
} from '@nt2/engine';
import { copyText, linkFor, type RoomLink } from '../net/room.js';
import { prefs, setPrefs } from '../state/prefs.js';
import { clear, el, on } from './dom.js';
import { icon } from './icons.js';
import { toast } from './toast.js';
import { versionLine } from '../version.js';
import { CONTROLLER } from './contact.js';
import { seriesPanel } from './panels.js';
import { ruleLine } from './dialogs.js';

/** Who publishes the game, as it appears in the small print. */
const OPERATOR = CONTROLLER.name;

export interface LobbyCallbacks {
  onName(name: string): void;
  onTakeSeat(seatId: string): void;
  onLeaveSeat(): void;
  onStart(): void;
  /** Open the match options, scrolled to one setting if named. */
  onOptions(focus?: string): void;
  onSettings(): void;
  onRules(): void;
  onSoloStart(): void;
  onCreateRoom(): void;
  onJoinRoom(code: string): void;
  onPrivacy(): void;
  onContact(): void;
  /** Back to the front door, giving up the table. */
  onBack(): void;
  /** Throw away this table's variations and play the standard game. */
  onStandardRules(): void;
  onTour(): void;
}

/**
 * Everything before the first tile is dealt.
 *
 * Two screens in one: the front door (play alone, host, or join) and the
 * waiting room (who is here, who is teamed with whom, and what the rules are
 * — written out, not listed as settings).
 */
export class Lobby {
  readonly root: HTMLElement;
  #body: HTMLElement;

  constructor(private cb: LobbyCallbacks) {
    this.#body = el('div', { class: 'lobby' });
    this.root = this.#body;
  }

  /** The front door, shown when there is no room yet. */
  renderHome(dictReady: boolean, dictError: string | null): void {
    clear(this.#body);
    this.#body.className = 'lobby lobby--home';
    this.#body.append(this.#hero());

    /*
     * A plain text field here makes Safari offer autofill, and its suggestion
     * chip lands squarely on top of the buttons below it. Telling the browser
     * this is a throwaway nickname — not a username it should remember — keeps
     * the chip away, and `enterkeyhint` gives the keyboard a Done key so it can
     * actually be dismissed on a phone.
     */
    const name = el('input', {
      class: 'home__nameinput', type: 'text', maxLength: 24, placeholder: 'What shall we call you?',
      value: prefs().name || nickname(), 'aria-label': 'Your name',
      name: 'nickname', autocomplete: 'off', autocorrect: 'off',
      autocapitalize: 'words', spellcheck: false, enterkeyhint: 'done',
      title: 'Made up for you. Change it if you like — it only tells the table who is who.',
    }) as HTMLInputElement;
    const commitName = (): void => {
      setPrefs({ name: name.value.trim() });
      this.cb.onName(name.value.trim());
    };
    on(name, 'change', commitName);
    on(name, 'keydown', (ev) => {
      if ((ev as KeyboardEvent).key !== 'Enter') return;
      ev.preventDefault();
      commitName();
      name.blur();                 // dismisses the on-screen keyboard
    });
    const joinCode = el('input', {
      class: 'input', type: 'text', maxLength: 200, placeholder: 'Paste the invite link',
      'aria-label': 'Invite link', autocapitalize: 'none',
      name: 'invite', autocomplete: 'off', autocorrect: 'off', spellcheck: false,
      enterkeyhint: 'go',
    }) as HTMLInputElement;

    const ready = (): boolean => {
      if (!dictReady) { toast('The word list is still loading…', 'info'); return false; }
      // Never a blocker: if the field has been emptied, another made-up name
      // is simply picked. Nobody is stopped from playing to supply a name.
      const n = name.value.trim() || nickname();
      name.value = n;
      setPrefs({ name: n });
      this.cb.onName(n);
      return true;
    };

    /*
     * Two ways in, as two cards side by side, each the whole of its button.
     *
     * This page has very little on it, and it used to spend that on a panel
     * with full-width bars in it and a paragraph under each — on a phone it
     * scrolled, with a gap in the middle. Two square cards say "pick one" at a
     * glance, sit in the thumb's reach, and leave the room behind them to be
     * seen. Joining somebody else's table is not a third way in: an invite is
     * a link, and opening it goes straight to the table. The paste box is kept
     * for a link that opened in a different browser from the installed game,
     * folded away behind a small link.
     */
    const card = (glyph: Parameters<typeof icon>[0], title: string,
      sub: string | null, note: string, label: string): HTMLButtonElement => {
      const b = el('button', { class: 'choice__btn', type: 'button', 'aria-label': label },
        el('span', { class: 'choice__icon', 'aria-hidden': 'true' }, icon(glyph, 20)),
        el('span', { class: 'choice__title', text: title }),
        sub ? el('span', { class: 'choice__sub', text: sub }) : null,
        el('span', { class: 'choice__note', text: note })) as HTMLButtonElement;
      return b;
    };
    const solo = card('play', 'Play Solo', null,
      'You against the bag. The best way to learn.', 'Play Solo');
    on(solo, 'click', () => { if (ready()) this.cb.onSoloStart(); });
    const host = card('players', 'Multiplayer', 'Create a table',
      'Share a link. Up to 20 can play, you too.', 'Multiplayer – Create a table');
    on(host, 'click', () => { if (ready()) this.cb.onCreateRoom(); });

    const join = el('button', { class: 'btn', type: 'button', text: 'Join' });
    on(join, 'click', () => {
      if (!ready()) return;
      const v = joinCode.value.trim();
      if (!v) { joinCode.focus(); return; }
      this.cb.onJoinRoom(v);
    });
    on(joinCode, 'keydown', (ev) => {
      if ((ev as KeyboardEvent).key === 'Enter') join.click();
    });
    const pasteRow = el('div', { class: 'lobby__join', hidden: true }, joinCode, join);
    const pasteLink = el('button', {
      class: 'lobby__paste', type: 'button', text: 'Have an invite link? Paste it here',
    });
    on(pasteLink, 'click', () => {
      pasteRow.hidden = !pasteRow.hidden;
      if (!pasteRow.hidden) joinCode.focus();
    });

    const nameRow = el('label', { class: 'home__name' },
      el('span', { class: 'home__namelabel', text: 'Playing as' }),
      name,
      el('span', { class: 'home__nameedit', 'aria-hidden': 'true', text: '✎' }));

    this.#body.append(el('div', { class: 'home' },
      nameRow,
      el('div', { class: 'choices' },
        el('div', { class: 'choice choice--solo' }, solo),
        el('div', { class: 'choice choice--host' }, host)),
      el('div', { class: 'lobby__pastewrap' }, pasteLink, pasteRow)));

    const rules = el('button', { class: 'lobby__link', type: 'button', text: 'Show me how to play' });
    on(rules, 'click', () => this.cb.onRules());
    const privacy = el('button', { class: 'lobby__link', type: 'button', text: 'Privacy and terms' });
    on(privacy, 'click', () => this.cb.onPrivacy());
    const settings = el('button', { class: 'lobby__link', type: 'button', text: 'Settings' });
    on(settings, 'click', () => this.cb.onSettings());

    // "Get in touch" lives inside Privacy and terms now, beside who runs it.
    this.#body.append(el('div', { class: 'lobby__foot' },
      el('div', { class: 'lobby__links' }, rules, el('span', { 'aria-hidden': 'true', text: '·' }),
        settings, el('span', { 'aria-hidden': 'true', text: '·' }), privacy),
      el('div', { class: 'lobby__version', text: `${versionLine()} · © 2026 ${OPERATOR}` })));

    if (dictError) {
      this.#body.append(el('div', { class: 'chip chip--bad', text: dictError }));
    } else if (!dictReady) {
      this.#body.append(el('div', { class: 'chip lobby__loading', text: 'Loading the word list…' }));
    }
  }

  /**
   * The name explains itself.
   *
   * A single tile carrying the P — the same one the app icon uses — sits
   * beside it, so the thing on the home screen and the thing on the page are
   * recognisably each other. Below, the expansion with its acronym letters
   * lifted out says what the game is in one line.
   */
  #hero(): HTMLElement {
    const mark = el('div', { class: 'wordmark__tile', 'aria-hidden': 'true' },
      el('span', { class: 'wordmark__letter', text: 'P' }),
      el('span', { class: 'wordmark__value', text: '3' }));
    return el('div', { class: 'lobby__hero' },
      el('div', { class: 'wordmark' },
        mark,
        el('h1', { class: 'lobby__title', text: BRAND.name })),
      el('p', { class: 'lobby__expand' }, ...acronym(BRAND.expansion)),
      el('p', { class: 'lobby__tag',
        text: 'A race to build your own crossword — no turns, everybody at once.' }));
  }

  /**
   * Arriving by invite link, before the table has answered.
   *
   * Brief on a good connection, but on a phone it can be a second or two, and
   * the front page in that gap made it look as though the link had done
   * nothing.
   */
  renderJoining(roomId: string): void {
    clear(this.#body);
    this.#body.className = 'lobby';
    this.#body.append(this.#hero());
    const back = el('button', { class: 'btn btn--ghost btn--sm', type: 'button', text: 'Cancel' });
    on(back, 'click', () => this.cb.onBack());
    this.#body.append(el('div', { class: 'lobby__card panel', style: 'text-align:center' },
      el('div', { class: 'joining__spin', 'aria-hidden': 'true' }),
      el('p', { style: 'margin:10px 0 4px;font-weight:700', text: `Joining table ${roomId}…` }),
      el('p', { class: 'hint', style: 'margin:0 0 12px', text: 'You were sent a link to this table. One moment.' }),
      back));
  }

  /**
   * The waiting room.
   *
   * `wire` is null for a table this device has just made and the server has
   * not yet answered for. Opening a brand-new room takes a WebSocket handshake
   * and the room starting up on the server — most of a second on a phone —
   * and the page used to sit unchanged for all of it, so pressing the button
   * looked as though it had not worked. The link is made here, not by the
   * server, so the room is drawn at once with Share already working; only
   * starting and the rules wait for the answer.
   */
  renderRoom(wire: WireState | null, link: RoomLink | null, isHost: boolean): void {
    clear(this.#body);
    this.#body.className = 'lobby';

    // Somewhere to go back to. Making a table used to be a one-way door: the
    // only way out was closing the tab.
    const back = el('button', {
      class: 'btn btn--sm btn--ghost lobby__back', type: 'button',
      title: 'Give up this table and go back to the start',
    }, icon('back', 15), el('span', { text: 'Back' }));
    on(back, 'click', () => this.cb.onBack());
    this.#body.append(el('div', { class: 'lobby__bar' }, back));

    this.#body.append(this.#hero());

    /*
     * Getting people in. Share is the main button: on a phone it opens the
     * system share sheet, which is how a link actually reaches anyone. Copying
     * is the fallback, and the only option where sharing is not available.
     *
     * No room code any more. Nobody could type it in — the key in the link is
     * what lets people join — so showing it only invited people to try. It is
     * in the players panel during the match, where it helps people check they
     * are all at the same table.
     */
    if (link) {
      const canShare = 'share' in navigator;
      const share = el('button', { class: 'btn btn--primary', type: 'button', text: 'Share' });
      share.hidden = !canShare;
      on(share, 'click', () => {
        void navigator.share?.({
          title: BRAND.name,
          text: `Join my ${BRAND.name} table — tap the link and you are in.`,
          url: linkFor(link),
        }).catch(() => { /* the user dismissed the sheet */ });
      });
      const copyLink = el('button', {
        class: canShare ? 'btn' : 'btn btn--primary', type: 'button', text: 'Copy the link',
      });
      on(copyLink, 'click', async () => {
        const ok = await copyText(linkFor(link));
        toast(ok ? 'Link copied' : 'Could not copy — select the link manually',
          ok ? 'good' : 'bad');
      });

      this.#body.append(el('div', { class: 'lobby__card panel lobby__invite' },
        el('div', { class: 'lobby__invitetext', text: 'Share the link with the other players.' }),
        el('div', { class: 'lobby__row' }, share, copyLink)));
    }

    if (!wire) {
      const me = el('div', { class: 'seatrow seatrow--mine' },
        el('span', { class: 'seatcard__dot' }),
        el('div', { class: 'seatrow__members' },
          el('span', { class: 'pill pill--host', text: `${prefs().name || 'You'} · host` })));
      const starting = el('button', {
        class: 'btn btn--primary', type: 'button', text: 'Setting up the table…', disabled: true,
      });
      const rulesBtn = el('button', { class: 'btn btn--ghost', type: 'button', text: 'How to play' });
      on(rulesBtn, 'click', () => this.cb.onRules());
      this.#body.append(el('div', { class: 'lobby__card panel lobby__pending' },
        el('div', { class: 'lobby__row lobby__actions' }, starting,
          el('button', { class: 'btn', type: 'button', text: 'Change the rules', disabled: true }),
          rulesBtn),
        el('div', { style: 'height:16px' }),
        el('div', { class: 'field' }, el('label', { text: 'Seats' }), el('div', { class: 'lobby__seats' }, me)),
        el('p', { class: 'hint lobby__connecting' },
          el('span', { class: 'joining__spin joining__spin--inline', 'aria-hidden': 'true' }),
          el('span', { text: 'Opening the table…' }))));
      this.#body.append(el('div', { class: 'lobby__version', text: versionLine() }));
      return;
    }

    // Before the next match: how the table stands so far.
    const series = seriesPanel(wire);
    if (series) this.#body.append(el('div', { class: 'lobby__card panel' }, series));

    // ---- seats ----
    const seats = el('div', { class: 'lobby__seats' });
    for (const seat of wire.seats) {
      const mine = seat.id === wire.youSeatId;
      const rowEl = el('div', { class: 'seatrow' + (mine ? ' seatrow--mine' : '') });
      const dot = el('span', { class: 'seatcard__dot' });
      dot.style.background = seat.colour;
      rowEl.append(dot);
      const members = el('div', { class: 'seatrow__members' });
      for (const id of seat.members) {
        const p = wire.players.find((x) => x.id === id);
        // Somebody whose connection has dropped is still at the table — most
        // likely switching back from sharing the link — so they are shown as
        // away rather than silently vanishing or looking present.
        const away = p?.connected === false;
        members.append(el('span', {
          class: 'pill' + (p?.isHost ? ' pill--host' : '') + (away ? ' pill--away' : ''),
          text: (p?.name ?? '?') + (p?.isHost ? ' · host' : '') + (away ? ' · away' : ''),
          title: away ? 'Their connection dropped. They keep their place until the match starts.' : '',
        }));
      }
      if (seat.members.length > 1) {
        members.append(el('span', { class: 'chip chip--good', text: 'team' }));
      }
      rowEl.append(members);
      if (!mine) {
        const joinSeat = el('button', {
          class: 'btn btn--sm', type: 'button', text: 'Team up',
          title: 'Share this seat — one rack, one grid, two of you',
        });
        on(joinSeat, 'click', () => this.cb.onTakeSeat(seat.id));
        rowEl.append(joinSeat);
      } else if (wire.seats.length > 1) {
        const leave = el('button', { class: 'btn btn--sm btn--ghost', type: 'button', text: 'Sit alone' });
        on(leave, 'click', () => this.cb.onLeaveSeat());
        rowEl.append(leave);
      }
      seats.append(rowEl);
    }

    const optionsBtn = el('button', {
      class: 'btn', type: 'button', text: 'Change the rules', disabled: !isHost,
      title: isHost ? 'Starting letters, rounds, word checking, scoring and the rest'
                    : 'Only the host can change these',
    });
    on(optionsBtn, 'click', () => this.cb.onOptions());
    const startBtn = el('button', {
      class: 'btn btn--primary', type: 'button',
      text: isHost ? 'Start the match' : 'Waiting for the host…',
      disabled: !isHost || wire.seats.length === 0,
    });
    on(startBtn, 'click', () => this.cb.onStart());
    const rulesBtn = el('button', { class: 'btn btn--ghost', type: 'button', text: 'How to play' });
    on(rulesBtn, 'click', () => this.cb.onRules());

    // At the top, so starting never means scrolling to find the button.
    this.#body.append(el('div', { class: 'lobby__card panel' },
      el('div', { class: 'lobby__row lobby__actions' }, startBtn, optionsBtn, rulesBtn),
      el('div', { style: 'height:16px' }),
      el('div', { class: 'field' },
        el('label', { text: `Seats — ${wire.seats.length} of ${MAX_SEATS}` }),
        seats,
        el('span', {
          class: 'hint',
          text: 'Everyone gets their own seat by default. Tap “team up” to share ' +
                'one rack and one grid with somebody — handy for playing ' +
                'alongside a child.',
        })),
      el('div', { style: 'height:16px' }),
      this.#rulesBlock(wire, isHost)));

    this.#body.append(el('div', { class: 'lobby__version', text: versionLine() }));
  }

  /**
   * The rules, as sentences.
   *
   * These used to be chips: `dumping on`, `3 checks each`, `no forced draw`.
   * Each named a setting rather than saying what it did, one of them was a
   * double negative, and a player who had not read the code could not tell
   * what any of them meant. Now it reads as prose, with the numbers a host
   * controls picked out inside it — so the same paragraph teaches the game and
   * shows the settings, and it is obvious which parts are somebody's choice.
   */
  #rulesBlock(wire: WireState, isHost: boolean): HTMLElement {
    const lines = describeRules(wire.config, wire.seats.length);
    const changed = changedFromDefaults(wire.config);

    // All of it, not a summary behind "And the rest…": these are the rules of
    // the match about to be played, and a player should not have to ask for
    // them.
    // The host can tap any yellow value to change it; for everyone else it is
    // simply the rule.
    const prose = el('div', { class: 'rules' }, ...lines.map((l) =>
      ruleLine(l, isHost ? (setting) => this.cb.onOptions(setting) : undefined)));

    const head = el('div', { class: 'lobby__row', style: 'justify-content:space-between' },
      el('label', { text: 'How this match will be played' }));

    if (changed.length > 0) {
      const badge = el('span', {
        class: 'chip chip--warn',
        title: changed.join('\n'),
        text: `${changed.length} change${changed.length === 1 ? '' : 's'} from the standard game`,
      });
      head.append(badge);
    } else {
      head.append(el('span', { class: 'chip chip--good', text: 'the standard game' }));
    }

    const foot = el('div', { class: 'lobby__row', style: 'margin-top:10px' });
    if (isHost) {
      foot.append(el('span', { class: 'hint', text: 'Tap any yellow value to change it.' }));
    }
    if (isHost && changed.length > 0) {
      const reset = el('button', {
        class: 'btn btn--sm', type: 'button', text: 'Use the standard rules',
        title: changed.join('\n'),
      });
      on(reset, 'click', () => this.cb.onStandardRules());
      foot.append(reset);
    }

    return el('div', { class: 'field' }, head, prose, foot);
  }
}


/**
 * Split a phrase into runs of capitals and everything else, so the letters the
 * acronym is built from can be lifted out of their own words.
 */
function acronym(phrase: string): Node[] {
  const out: Node[] = [];
  for (const part of phrase.split(/([A-Z]+)/)) {
    if (!part) continue;
    out.push(/^[A-Z]+$/.test(part)
      ? el('b', { class: 'lobby__initial', text: part })
      : document.createTextNode(part));
  }
  return out;
}
